import { UntypedFormControl } from '@angular/forms';

import {
  CONTAINED_IN,
  CONTAINS,
  EXCLUDES,
  INCLUDES,
  START_WITH,
  IS_EQUAL_TO,
  IS_GREATER_OR_EQUAL_THAN,
  IS_GREATER_THAN,
  IS_LESS_OR_EQUAL_THAN,
  IS_LESS_THAN,
  IS_NOT_EQUAL_TO,
  NOT_CONTAINED_IN,
  NOT_CONTAINED_IN_PQL,
  NOT_CONTAINS,
} from './preset-condition.constant';
import {
  BooleanSelectFieldComponent,
  DatepickerFieldNewComponent,
  TextFieldComponent,
  SelectComponent,
  SelectWithIconComponent,
  TagsFieldComponent,
  NumberFieldComponent,
} from '@platform500services/p500-ui-kit';
import { IParsedDataResponse } from '../services/data/data-service.interface';
import { ComponentModel } from '../services/actions/models/field.model';
import { ConditionTypes } from './preset-condition.interface';
import { hoursFromData } from '../components/form-fields/pql-editor-dynamic/data.constants';
import { DragDropSelectComponent } from '../components/form-fields/drag-drop-select/drag-drop-select.component';
import { EARNING_STATUS_TRANSLATIONS, INVOICE_STATUSES_TRANSLATIONS } from '../services/data/data-constants-translations';
import { Condition } from '../../modules/reporting/drilldown2/domain/settings';

function getComponent(
  control: UntypedFormControl,
  key: string,
  type: ConditionTypes = '',
  label: string = '',
  globalData: IParsedDataResponse
): ComponentModel {
  switch (type) {
    case 'text':
    case '':
    case 'pql-text':
      return {
        component: TextFieldComponent,
        settings: {
          control,
          label,
          key,
        },
      };
    case 'number':
      return {
        component: TagsFieldComponent,
        settings: {
          control,
          label,
          key,
          onlyNumbers: true,
        },
      };
    case 'pql-number':
      return {
        component: NumberFieldComponent,
        settings: {
          control,
          label,
          key,
        },
      };
    case 'datetime':
      return {
        component: DatepickerFieldNewComponent,
        settings: {
          control,
          customClassDatepicker: '-fill',
          label,
          key,
        },
      };
    case 'boolean':
      return {
        component: BooleanSelectFieldComponent,
        settings: {
          control,
          label,
          key,
        },
      };
    case 'pql-boolean':
      return {
        component: BooleanSelectFieldComponent,
        settings: {
          control,
          label,
          key,
          data: new Map([
            [false, $localize`:@@common.no:No`],
            [true, $localize`:@@common.yes:Yes`],
          ]),
        },
      };
    case 'country':
      return {
        component: SelectWithIconComponent,
        settings: {
          data: globalData.countriesWithoutAll,
          control,
          label: $localize`:@@common.countries:Countries`,
          multiple: true,
          key,
        },
      };
    case 'user-status':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.user_status,
          multiple: false,
          control,
          label,
          key,
        },
      };
    case 'advertisers-name':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.advertisers,
          multiple: true,
          control,
          label: $localize`:@@common.advertisers:Advertisers`,
          key,
        },
      };
    case 'affiliates-name':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.affiliates,
          multiple: true,
          control,
          label: $localize`:@@common.affiliates:Affiliates`,
          key,
        },
      };
    case 'affiliates-managers-name':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.affiliate_managers,
          multiple: true,
          control,
          label: $localize`:@@affiliate.affiliates_managers:Affiliates Managers`,
          key,
        },
      };
    case 'offers-name':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.offers,
          multiple: true,
          control,
          label: $localize`:@@common.offers:Offers`,
          key,
        },
      };
    case 'goal-payout':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.payout_type,
          multiple: true,
          control,
          label: $localize`:@@common.payout_type:Payout Type`,
          key,
        },
      };
    case 'goal-revenue':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.revenue_type,
          multiple: true,
          control,
          label: $localize`:@@common.revenue_type:Revenue Type`,
          key,
        },
      };
    case 'goal-name':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.goals,
          multiple: true,
          control,
          label: $localize`:@@common.goals:Goals`,
          key,
        },
      };
    case 'goal-type':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.goal_types,
          multiple: true,
          control,
          label: $localize`:@@common.goal_type:Goal Type`,
          key,
        },
      };
    case 'earning-monetary-type':
      return {
        component: SelectComponent,
        settings: {
          data: { conversion: $localize`:@@common.conversion:Conversion`, transaction: $localize`:@@common.transaction:Transaction` },
          multiple: true,
          control,
          label: $localize`:@@earning.monetary_type:Monetary Type`,
          key,
        },
      };
    case 'conversion-tracking':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.conversion_tracking_type,
          multiple: true,
          control,
          label: $localize`:@@conversion.tracking_type:Conversion Tracking Type`,
          key,
        },
      };
    case 'language-code':
      return {
        component: SelectWithIconComponent,
        settings: {
          data: globalData.languages,
          multiple: true,
          control,
          label: $localize`:@@common.language:Language`,
          key,
        },
      };
    case 'tags-multiple':
      return {
        component: TagsFieldComponent,
        settings: {
          multiple: true,
          control,
          label,
          key,
        },
      };
    case 'offers-url':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.offer_urls,
          control,
          label: $localize`:@@offer.offers_url:Offers URL`,
          multiple: true,
          key,
        },
      };
    case 'day-of-week':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.dayOfWeek,
          multiple: true,
          control,
          label: $localize`:@@dates.day_of_week:Day of Week`,
          key,
        },
      };
    case 'hour-of-day':
      return {
        component: SelectComponent,
        settings: {
          data: hoursFromData,
          control,
          label: $localize`:@@dates.hour_of_day:Hour Of Day`,
          key,
        },
      };
    case 'type-earning-status':
      return {
        component: SelectComponent,
        settings: {
          data: EARNING_STATUS_TRANSLATIONS,
          control,
          label: $localize`:@@earning.status:Earning Status`,
          key,
        },
      };
    case 'type-invoice-status':
      return {
        component: SelectComponent,
        settings: {
          data: INVOICE_STATUSES_TRANSLATIONS,
          control,
          label: $localize`:@@invoice.status:Invoice Status`,
          key,
        },
      };
    case 'type-payment-status':
      return {
        component: SelectComponent,
        settings: {
          data: {
            draft: $localize`:@@common.draft:Draft`,
            declined: $localize`:@@common.declined:Declined`,
            approved: $localize`:@@common.approved:Approved`,
            sent: $localize`:@@common.sent:Sent`,
            arrived: $localize`:@@common.arrived:Arrived`,
            paid: $localize`:@@common.paid:Paid`,
          },
          control,
          label: $localize`:@@finance.payment_status:Payment Status`,
          key,
        },
      };
    case 'payout_models':
      return {
        component: SelectComponent,
        settings: {
          data: Object.keys(globalData.payout_models).reduce((acc, key) => ({ ...acc, [key]: globalData.payout_models[key].name }), {}),
          multiple: true,
          control,
          label: $localize`:@@common.payout_models:Payout Models`,
          key,
        },
      };
    case 'revenue_models':
      return {
        component: SelectComponent,
        settings: {
          data: Object.keys(globalData.revenue_models).reduce((acc, key) => ({ ...acc, [key]: globalData.revenue_models[key].name }), {}),
          multiple: true,
          control,
          label: $localize`:@@common.revenue_models:Revenue Models`,
          key,
        },
      };
    case 'payment-system': {
      const defaultPayload = Object.values(globalData.default_payment_systems_list).find((system) => system.name === label)?.currencies;
      const availableCurrencies = defaultPayload ?? Object.keys(globalData.currency_list);

      const data = {};
      availableCurrencies.forEach((currency) => {
        data[currency] = `${globalData.currency_list[currency].name} / ${globalData.currency_list[currency].code}`;
      });

      return {
        component: DragDropSelectComponent,
        settings: {
          data,
          control,
          label: $localize`:@@common.currency:Currency`,
          key,
        },
      };
    }
    case 'transaction-types': {
      return {
        component: SelectComponent,
        settings: {
          data: globalData.transaction_types,
          control,
          label: $localize`:@@transaction.types:Transactions Types`,
          multiple: true,
          key,
        },
      };
    }
    case 'pql-date': {
      return {
        component: SelectComponent,
        settings: {
          data: {
            previous_months: $localize`:@@dates.previous_months:Previous Months`,
            current_month: $localize`:@@common.current_month:Current Month`,
          },
          control,
          label: 'Date',
          key,
        },
      };
    }
    case 'affiliate_managers':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.affiliate_managers,
          multiple: false,
          control,
          label: $localize`:@@affiliate.manager:Affiliate Manager`,
          key,
        },
      };
    case 'type-earning-payment-method':
      return {
        component: SelectComponent,
        settings: {
          data: globalData.payment_systems_list,
          multiple: false,
          control,
          label: $localize`:@@payment.method:Payment Method`,
          key,
        },
      };
    default:
      if (type in globalData) {
        return {
          component: SelectComponent,
          settings: {
            data: globalData[type],
            multiple: true,
            control,
            label,
            key,
          },
        };
      }
      return {
        component: TextFieldComponent,
        settings: {
          control,
          label,
          key,
        },
      };
  }
}

function getSignsData(type?: ConditionTypes): { [key: string]: string } {
  switch (type) {
    case 'like-text':
      return {
        ...CONTAINS,
        ...NOT_CONTAINS,
      };
    case 'type-earning-payment-method':
      return {
        ...IS_EQUAL_TO,
        ...IS_NOT_EQUAL_TO,
      };
    default:
      return {
        ...CONTAINED_IN,
        ...NOT_CONTAINED_IN,
      };
  }
}

function getSignsDataPql(type: ConditionTypes): { [key: string]: string } {
  let mappedSigns = {};
  switch (type) {
    case 'number':
    case 'text':
    case 'boolean':
      mappedSigns = {
        ...mappedSigns,
        ...IS_EQUAL_TO,
        ...IS_NOT_EQUAL_TO,
      };
      break;
  }
  if (type === 'text') {
    mappedSigns = {
      ...mappedSigns,
      ...CONTAINS,
    };
  }
  if (type === 'hour-of-day') {
    mappedSigns = {
      ...mappedSigns,
      ...IS_GREATER_THAN,
      ...IS_LESS_THAN,
      ...IS_GREATER_OR_EQUAL_THAN,
      ...IS_LESS_OR_EQUAL_THAN,
    };
  }
  if (type !== 'boolean' && type !== 'hour-of-day') {
    mappedSigns = {
      ...mappedSigns,
      ...CONTAINED_IN,
      ...NOT_CONTAINED_IN_PQL,
    };
  }
  if (type === 'pql-text' || type === 'pql-email') {
    mappedSigns = {
      ...IS_EQUAL_TO,
      ...IS_NOT_EQUAL_TO,
    };
  }
  if (type === 'pql-number') {
    mappedSigns = {
      ...IS_EQUAL_TO,
      ...IS_NOT_EQUAL_TO,
      ...IS_GREATER_THAN,
      ...IS_LESS_THAN,
      ...IS_GREATER_OR_EQUAL_THAN,
      ...IS_LESS_OR_EQUAL_THAN,
    };
  }
  if (type === 'pql-boolean') {
    mappedSigns = {
      ...IS_EQUAL_TO,
      ...IS_NOT_EQUAL_TO,
    };
  }
  if (type === 'payment-system') {
    mappedSigns = {
      ...START_WITH,
      ...INCLUDES,
      ...EXCLUDES,
    };
  }
  if (type === 'pql-date') {
    mappedSigns = {
      ...IS_EQUAL_TO,
    };
  }
  return mappedSigns;
}
function addCurrencyCondition(conditions: Condition[], currency: string): Condition[] {
  if (!currency) {
    return conditions;
  }
  const currencyCondition = {
    value: currency,
    key: 'currency_code',
    label: $localize`:@@common.currency:Currency`,
    type: 'currency-select',
    config: 'AND',
    operator: 'IN',
  };
  const currencyConditionIndex = conditions.findIndex((condition) => condition.key === 'currency_code');
  if (currencyConditionIndex === -1) {
    return conditions.concat([currencyCondition]);
  } else {
    conditions[currencyConditionIndex] = currencyCondition;
    return conditions;
  }
}
export { getComponent, getSignsData, getSignsDataPql, addCurrencyCondition };
